// src/pages/Purchases.tsx
// Purchase tracking page for recording purchases and linking them to inventory items

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Product } from '../services/productCatalogAPI';
import { ShoppingCart, Plus, Search, Calendar, DollarSign, Eye, AlertCircle, Loader2 } from 'lucide-react';
import RecordPurchaseModal from '../components/purchases/RecordPurchaseModal';
import PurchaseDetailModal from '../components/purchases/PurchaseDetailModal';
import AllocationProgressBar from '../components/purchases/AllocationProgressBar';
import { purchasesAPI } from '../services/purchasesAPI';
import { categoryAPI } from '../services/categoryAPI';
import { inventoryAPI } from '../services/inventoryAPI';
import { Purchase } from '../types/purchase';
import { InventoryCategory } from '../types/category';
import { logger } from '../utils/logger';

const purchasesLogger = logger.createServiceLogger('PurchasesPage');

const Purchases: React.FC = () => {
  const location = useLocation();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [initialProducts, setInitialProducts] = useState<Product[] | undefined>(undefined);
  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState<'all' | '30days' | '90days' | 'year'>('all');
  
  const [isRecordModalOpen, setIsRecordModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState<Purchase | null>(null);
  const [linkedItems, setLinkedItems] = useState<any[]>([]);
  const [loadingLinkedItems, setLoadingLinkedItems] = useState(false);
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);

  useEffect(() => {
    loadPurchases();
    loadCategories();
  }, []);

  // Detect products passed from Product Catalog page
  useEffect(() => {
    const state = location.state as { productsToAdd?: Product[]; fromProductCatalog?: boolean } | null;
    
    if (state?.fromProductCatalog && state?.productsToAdd?.length) {
      purchasesLogger.info('Products received from Product Catalog', { 
        count: state.productsToAdd.length 
      });
      setInitialProducts(state.productsToAdd);
      setIsRecordModalOpen(true);
      
      // Clear the location state to prevent re-triggering on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const loadPurchases = async () => {
    setLoading(true);
    setError(null);
    
    try {
      purchasesLogger.debug('Loading all purchases');
      const response = await purchasesAPI.getPurchases();
      
      if (response.success && response.data) {
        setPurchases(response.data);
        purchasesLogger.info('Purchases loaded successfully', { count: response.data.length });
      } else {
        setError(response.error?.message || 'Failed to load purchases');
        purchasesLogger.error('Failed to load purchases', { error: response.error });
      }
    } catch (err: any) {
      purchasesLogger.error('Exception loading purchases', undefined, err);
      setError(err.message || 'Failed to load purchases');
    } finally {
      setLoading(false);
    }
  };

  const loadLinkedItems = async (purchaseId: string) => {
    setLoadingLinkedItems(true);
    try {
      purchasesLogger.debug('Loading linked items for purchase', { purchaseId });
      const response = await inventoryAPI.getItemsByPurchase(purchaseId);
      
      if (response.success && response.data) {
        setLinkedItems(response.data);
        purchasesLogger.info('Linked items loaded', { count: response.data.length });
      } else {
        setLinkedItems([]);
        purchasesLogger.warn('No linked items found', { purchaseId });
      }
    } catch (err: any) {
      purchasesLogger.error('Error loading linked items', undefined, err);
      setLinkedItems([]);
    } finally {
      setLoadingLinkedItems(false);
    }
  };

  const loadCategories = async () => {
    setLoadingCategories(true);
    try {
      purchasesLogger.debug('Loading categories for line item modals');
      const response = await categoryAPI.getAllCategories();
      
      if (response.success && response.data) {
        const tree = categoryAPI.buildCategoryTree(response.data.categories);
        
        const flatCategories: InventoryCategory[] = [];
        tree.forEach(parent => {
          flatCategories.push(parent);
          if (parent.children && parent.children.length > 0) {
            parent.children.forEach(child => {
              flatCategories.push(child);
            });
          }
        });
        
        setCategories(flatCategories);
        purchasesLogger.info('Categories loaded successfully', { 
          total: flatCategories.length,
          parents: tree.length,
          subcategories: flatCategories.filter(c => c.parentCategoryId).length
        });
      } else {
        purchasesLogger.warn('Failed to load categories', { error: response.error });
      }
    } catch (err: any) {
      purchasesLogger.error('Error loading categories', undefined, err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleRecordPurchaseComplete = async () => {
    await loadPurchases();
    setIsRecordModalOpen(false);
    showNotification('success', 'Purchase recorded successfully');
  };

  const handleViewPurchase = async (purchase: Purchase) => {
    setSelectedPurchase(purchase);
    setIsDetailModalOpen(true);
    await loadLinkedItems(purchase.purchaseId);
  };

  const handlePurchaseUpdated = async () => {
    await loadPurchases();
    purchasesLogger.info('Purchase list refreshed after update');
  };

  const handleEditPurchase = async (purchaseId: string, updates: any) => {
    try {
      purchasesLogger.debug('Updating purchase', { purchaseId, updates });
      const response = await purchasesAPI.updatePurchase(purchaseId, updates);
      
      if (response.success && response.data) {
        setPurchases(prev => prev.map(p => 
          p.purchaseId === purchaseId ? response.data! : p
        ));
        showNotification('success', 'Purchase updated successfully');
        purchasesLogger.info('Purchase updated successfully', { purchaseId });
        
        if (selectedPurchase?.purchaseId === purchaseId) {
          setSelectedPurchase(response.data);
        }
      } else {
        showNotification('error', `Failed to update purchase: ${response.error?.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      purchasesLogger.error('Error updating purchase', undefined, err);
      showNotification('error', `Failed to update purchase: ${err.message}`);
    }
  };

  const handleDeletePurchase = async (purchase: Purchase) => {
    if (!window.confirm(`Are you sure you want to delete this purchase?\n\nVendor: ${purchase.vendor || 'Unknown'}\nDate: ${new Date(purchase.purchaseDate).toLocaleDateString()}\nCost: $${purchase.totalCost.toFixed(2)}`)) {
      return;
    }

    try {
      purchasesLogger.debug('Deleting purchase', { purchaseId: purchase.purchaseId });
      const response = await purchasesAPI.deletePurchase(purchase.purchaseId);
      
      if (response.success) {
        setPurchases(prev => prev.filter(p => p.purchaseId !== purchase.purchaseId));
        setIsDetailModalOpen(false);
        setSelectedPurchase(null);
        showNotification('success', 'Purchase deleted successfully');
        purchasesLogger.info('Purchase deleted successfully', { purchaseId: purchase.purchaseId });
      } else {
        showNotification('error', `Failed to delete purchase: ${response.error?.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      purchasesLogger.error('Error deleting purchase', undefined, err);
      showNotification('error', `Failed to delete purchase: ${err.message}`);
    }
  };

  const getDateFilterDate = () => {
    const now = new Date();
    switch (dateFilter) {
      case '30days':
        return new Date(now.setDate(now.getDate() - 30));
      case '90days':
        return new Date(now.setDate(now.getDate() - 90));
      case 'year':
        return new Date(now.setFullYear(now.getFullYear() - 1));
      default:
        return null;
    }
  };

  const filteredPurchases = purchases.filter(purchase => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      (purchase.vendor?.toLowerCase() || '').includes(searchLower) ||
      (purchase.poNumber?.toLowerCase() || '').includes(searchLower);
    
    const filterDate = getDateFilterDate();
    const matchesDate = !filterDate || new Date(purchase.purchaseDate) >= filterDate;
    
    return matchesSearch && matchesDate;
  });

  const totalSpent = filteredPurchases.reduce((sum, p) => sum + p.totalCost, 0);
  const totalPurchases = filteredPurchases.length;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Purchase Tracking</h2>
          <p className="text-gray-600 mt-1">Record and manage equipment purchases</p>
        </div>
        <button 
          onClick={() => setIsRecordModalOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors shadow-md"
        >
          <Plus className="w-4 h-4 mr-2" />
          Record Purchase
        </button>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`p-4 rounded-md ${
          notification.type === 'success' 
            ? 'bg-green-100 border border-green-400 text-green-700' 
            : 'bg-red-100 border border-red-400 text-red-700'
        }`}>
          <div className="flex justify-between items-center">
            <span>{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="text-current hover:opacity-75"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Error Loading Purchases</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              <button 
                onClick={loadPurchases}
                className="text-sm text-red-800 underline hover:no-underline mt-2"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Purchases</h3>
          <p className="text-gray-600">Fetching purchase records...</p>
        </div>
      ) : (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Purchases</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{totalPurchases}</p>
                </div>
                <ShoppingCart className="w-8 h-8 text-blue-600" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Spent</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">${totalSpent.toFixed(2)}</p>
                </div>
                <DollarSign className="w-8 h-8 text-green-600" />
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative max-w-sm">
                <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by vendor or PO number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-blue-500"
                />
              </div>

              {/* Date Filter */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                {[
                  { key: 'all', label: 'All Time' },
                  { key: '30days', label: 'Last 30 Days' },
                  { key: '90days', label: 'Last 90 Days' },
                  { key: 'year', label: 'Last Year' }
                ].map((filter) => (
                  <button
                    key={filter.key}
                    onClick={() => setDateFilter(filter.key as any)}
                    className={`px-3 py-1 rounded-md text-sm transition-colors whitespace-nowrap ${
                      dateFilter === filter.key
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Filter summary */}
            {(searchTerm || dateFilter !== 'all') && (
              <div className="mt-3 text-sm text-gray-600">
                Showing {filteredPurchases.length} of {purchases.length} purchases
                {searchTerm && <span> matching "{searchTerm}"</span>}
              </div>
            )}
          </div>

          {/* Purchases Table */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            {filteredPurchases.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingCart className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {purchases.length === 0 ? 'No purchases recorded yet' : 'No purchases found'}
                </h3>
                <p className="text-gray-600">
                  {purchases.length === 0 
                    ? 'Record your first purchase to start tracking equipment acquisitions.' 
                    : 'Try adjusting your search or filter criteria.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vendor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        PO Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Line Items
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                        Allocation
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Cost
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredPurchases.map((purchase) => (
                      <tr 
                        key={purchase.purchaseId} 
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => handleViewPurchase(purchase)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                            {new Date(purchase.purchaseDate).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="font-medium text-gray-900">{purchase.vendor || 'N/A'}</div>
                          {purchase.invoiceNumber && (
                            <div className="text-gray-500 text-xs">INV: {purchase.invoiceNumber}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {purchase.poNumber || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {purchase.totalLineItems || 0} items
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <AllocationProgressBar 
                            allocated={purchase.allocatedQuantity || 0} 
                            total={purchase.totalQuantity || purchase.quantity || 0} 
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          ${purchase.totalCost.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
                            <button 
                              onClick={() => handleViewPurchase(purchase)}
                              className="p-1 text-gray-400 hover:text-blue-600 transition-colors" 
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Record Purchase Modal */}
      <RecordPurchaseModal
        isOpen={isRecordModalOpen}
        onClose={() => {
          setIsRecordModalOpen(false);
          setInitialProducts(undefined);
        }}
        onSave={handleRecordPurchaseComplete}
        categories={categories}
        initialProducts={initialProducts}
      />

      {/* Purchase Detail Modal */}
      {selectedPurchase && (
        <PurchaseDetailModal
          isOpen={isDetailModalOpen}
          purchase={selectedPurchase}
          categories={categories}
          linkedItems={linkedItems}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedPurchase(null);
            setLinkedItems([]);
          }}
          onEdit={() => handleEditPurchase(selectedPurchase.purchaseId, {})}
          onDelete={() => handleDeletePurchase(selectedPurchase)}
          onItemsCreated={() => loadLinkedItems(selectedPurchase.purchaseId)}
          onPurchaseUpdated={handlePurchaseUpdated}
        />
      )}
    </div>
  );
};

export default Purchases;
